/**
 * flexible-modal.js
 * ---------------------------------------------
 * Custom element <flexible-modal> for the Flexible Modal section.
 *
 * Extends BasicModal (which itself extends ModalComponent) so it inherits:
 *   - body lock counter (shared via clearModalComponentCount)
 *   - focus trap (via FoxTheme.a11y.trapFocus / removeTrapFocus)
 *   - keyup-ESC listener
 *   - [aria-controls=<id>] auto-binding of open / close / overlay buttons
 *   - body-append behavior on show (for top-level z-index stacking)
 *
 * Trigger:
 *   Any <a href="#<trigger_id>"> on the page opens the matching modal,
 *   without requiring the link to declare aria-controls / aria-expanded.
 *
 * Nested modals:
 *   When a child modal (e.g. Quick View opened from a product card's
 *   "Choose options" button) is stacked on top of this modal, hide()
 *   is deferred so a single ESC press closes only the topmost modal,
 *   not both at once. See _hasStackedChildModal() below.
 */

(function () {
  if (typeof BasicModal === 'undefined') return;
  if (customElements.get('flexible-modal')) return;

  customElements.define(
    'flexible-modal',
    class FlexibleModal extends BasicModal {
      constructor() {
        super();
      }

      /**
       * Keep the element in place inside its overlay section while the
       * theme editor is open (so the editor can select / outline it),
       * but append to <body> on the storefront so multiple modals can
       * stack correctly with z-index.
       */
      get requiresBodyAppended() {
        return !(
          Shopify.designMode &&
          this.closest('.section-group-overlay-flexible-modal')
        );
      }

      /**
       * Defer hide while another modal is stacked on top of this one.
       *
       * Both this modal and the stacked child (e.g. Quick View) register
       * their own keyup-ESC listener on `document`. Without this guard a
       * single ESC press calls hide() on both modals in the same tick
       * and closes everything at once. Standard modal-in-modal UX is for
       * ESC to close the topmost modal first.
       *
       * This also prevents the parent modal from being dismissed via any
       * other code path (e.g. design-mode section deselect) while a child
       * modal is still open, which would leave the child orphaned.
       */
      hide() {
        if (this._hasStackedChildModal()) {
          return;
        }
        return super.hide();
      }

      /**
       * Returns true if any [open] element is positioned after this modal
       * inside <body>. Modal components are appended to <body> in the order
       * they are opened (later opens = later in DOM = visually on top), so
       * "is after us in body children" reliably means "is stacked above us".
       */
      _hasStackedChildModal() {
        if (this.parentElement !== document.body) return false;
        const bodyChildren = Array.from(document.body.children);
        const ourIdx = bodyChildren.indexOf(this);
        if (ourIdx === -1) return false;
        for (let i = ourIdx + 1; i < bodyChildren.length; i++) {
          const sibling = bodyChildren[i];
          if (sibling && sibling.hasAttribute && sibling.hasAttribute('open')) {
            return true;
          }
        }
        return false;
      }
    }
  );

  /**
   * Global hash-link trigger.
   *
   * Anywhere on the page, <a href="#some-id"> where #some-id points to a
   * <flexible-modal> element will open that modal on click.
   */
  document.addEventListener('click', function (event) {
    const link = event.target.closest('a[href]');
    if (!link) return;

    const href = link.getAttribute('href');
    if (!href) return;

    const hashIndex = href.lastIndexOf('#');
    if (hashIndex === -1) return;

    const id = href.slice(hashIndex + 1);
    if (!id || /[^\w-]/.test(id)) return;

    let target;
    try {
      target = document.getElementById(id);
    } catch (err) {
      return;
    }
    if (!target) return;
    if (target.tagName.toLowerCase() !== 'flexible-modal') return;

    event.preventDefault();

    if (target.hasAttribute('open')) return;
    if (typeof target.show === 'function') {
      target.show(link);
    }
  });
})();
